/**
 * Per-browser UI preferences of the sportsbook, in localStorage under one key: the desktop
 * slip open or closed, and the compact sidebar preference.
 *
 * Safe by construction:
 * - only the preferences listed in DEFAULTS, each with the type of its default (booleans
 *   today): nothing personal, nothing the server reads;
 * - localStorage, not a cookie: it never travels with the requests, it stays in the
 *   browser for the iframe's origin (and, in current browsers, for the embedding site);
 * - a value that is missing, of the wrong type or unreadable gives the default.
 * Storage can be unavailable (a third-party iframe with storage blocked, private mode,
 * quota): then the defaults apply and the first failure is logged.
 */
const STORAGE_KEY = 'sport-rev-iframe:uiPreferences';

export const UI_PREFERENCES = Object.freeze({
    // Desktop slip column open (CartMarketView; not the terminal, a shared device)
    SLIP_OPEN: 'slipOpen',
    // Left sidebar kept compact (icons only). Ready for the compact sidebar of the UI team
    // (Callshot change request §3): read it when the sidebar mounts, save it on the toggle.
    SIDEBAR_COMPACT: 'sidebarCompact',
});

const DEFAULTS = Object.freeze({
    [UI_PREFERENCES.SLIP_OPEN]: true,
    [UI_PREFERENCES.SIDEBAR_COMPACT]: false,
});

let storageWarned = false;

function warnOnce(action, err) {
    if (storageWarned) return;
    storageWarned = true;
    console.warn(`[uiPreferences] storage not available (${action}), defaults in use:`, err);
}

function browserStorage() {
    try {
        return typeof window !== 'undefined' ? window.localStorage : null;
    } catch (err) {
        warnOnce('access', err);
        return null;
    }
}

function readAll(storage) {
    try {
        const parsed = JSON.parse(storage.getItem(STORAGE_KEY) || '{}');
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (err) {
        warnOnce('read', err);
        return {};
    }
}

/**
 * @param {string} name - one of UI_PREFERENCES
 * @param {Storage} [storage] - injected by the tests
 * @returns {*} the saved value, or the default
 */
export function getPreference(name, storage = browserStorage()) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULTS, name)) return undefined;
    if (!storage) return DEFAULTS[name];
    const value = readAll(storage)[name];
    return typeof value === typeof DEFAULTS[name] ? value : DEFAULTS[name];
}

/**
 * @param {string} name - one of UI_PREFERENCES
 * @param {*} value - of the same type as the default
 * @param {Storage} [storage] - injected by the tests
 * @returns {boolean} true when saved
 */
export function setPreference(name, value, storage = browserStorage()) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULTS, name) || typeof value !== typeof DEFAULTS[name] || !storage) return false;
    try {
        const all = readAll(storage);
        all[name] = value;
        storage.setItem(STORAGE_KEY, JSON.stringify(all));
        return true;
    } catch (err) {
        warnOnce('write', err);
        return false;
    }
}
